import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import type { ExecutionList, ExecutionTrace } from '@agentic-os/contracts';
import { StructuredValue } from '@/components/assessed-view';
import { IfsDraftView, isIfsDraft } from '@/components/ifs-draft-view';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Button,
  ErrorNote,
  Field,
  Mono,
  PageHeader,
  Table,
  Td,
  Th,
} from '@/components/ui/primitives';
import { DomainStatus } from '@/components/ui/status-badge';
import { api, durationMs, formatTime } from '@/lib/api';

export function ExecutionsPage() {
  const q = useInfiniteQuery({
    queryKey: ['executions'],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      api<ExecutionList>(`/api/v1/executions?limit=25${pageParam ? `&before=${pageParam}` : ''}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 15_000,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Executions"
        description="Every workflow run in your organization, newest first."
      />
      <Card>
        <CardBody className="p-0">
          {q.isError ? (
            <div className="p-4">
              <ErrorNote error={q.error} />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Execution</Th>
                  <Th>Workflow</Th>
                  <Th>Status</Th>
                  <Th>Principal</Th>
                  <Th>Client</Th>
                  <Th>Started</Th>
                  <Th className="text-right">Duration</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.executionId}>
                    <Td>
                      <Link
                        to="/executions/$executionId"
                        params={{ executionId: e.executionId }}
                        className="text-accent hover:underline"
                      >
                        <Mono>{e.executionId.slice(0, 8)}…</Mono>
                      </Link>
                    </Td>
                    <Td className="whitespace-nowrap">
                      {e.workflow.key} <span className="text-muted">v{e.workflow.version}</span>
                    </Td>
                    <Td>
                      <DomainStatus value={e.status} />
                    </Td>
                    <Td>{e.principal.email ?? <Mono>{e.principal.userId}</Mono>}</Td>
                    <Td>
                      <Mono>{e.client.clientId}</Mono>
                    </Td>
                    <Td className="whitespace-nowrap text-muted">
                      {formatTime(e.startedAt ?? e.createdAt)}
                    </Td>
                    <Td className="text-right text-muted tabular-nums">
                      {durationMs(e.startedAt, e.completedAt)}
                    </Td>
                  </tr>
                ))}
                {!q.isPending && items.length === 0 && (
                  <tr>
                    <Td colSpan={7} className="text-muted">
                      No executions yet.
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
      {q.hasNextPage && (
        <Button onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
          Load more
        </Button>
      )}
    </div>
  );
}

/** Fields the detail view reads from workflow results (all optional). */
interface ResultShape {
  synthetic?: boolean;
  verification?: {
    claims?: number;
    verified?: number;
    downgraded?: number;
    droppedEvidence?: number;
  };
}

export function ExecutionDetailPage() {
  const { executionId } = useParams({ from: '/executions/$executionId' });
  const q = useQuery({
    queryKey: ['execution', executionId],
    queryFn: () => api<ExecutionTrace>(`/api/v1/executions/${executionId}/trace`),
    refetchInterval: (query) =>
      ['pending', 'running'].includes(query.state.data?.status ?? '') ? 2_000 : false,
  });
  if (q.isError) return <ErrorNote error={q.error} />;
  const d = q.data;
  const output: ResultShape | null = d?.output ?? null;
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title={d ? `${d.workflow.key} v${d.workflow.version}` : 'Execution'}
        description={<Mono>{executionId}</Mono>}
        actions={
          d && (
            <div className="flex gap-2">
              <DomainStatus value={d.status} />
              {d.outcome && <DomainStatus value={d.outcome} />}
            </div>
          )
        }
      />
      {d && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Trace</CardTitle>
            </CardHeader>
            <CardBody>
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Execution ID" value={<Mono>{d.executionId}</Mono>} />
                <Field
                  label="Workflow / version"
                  value={`${d.workflow.key} v${d.workflow.version}`}
                />
                <Field label="Status" value={<DomainStatus value={d.status} />} />
                <Field label="Correlation ID" value={<Mono>{d.correlationId}</Mono>} />
                <Field
                  label="Principal"
                  value={d.principal.email ?? <Mono>{d.principal.userId}</Mono>}
                />
                <Field
                  label="Client"
                  value={<Mono>{`${d.client.clientId} (${d.client.kind})`}</Mono>}
                />
                <Field label="Started" value={formatTime(d.startedAt)} />
                <Field
                  label="Completed"
                  value={`${formatTime(d.completedAt)} (${durationMs(d.startedAt, d.completedAt)})`}
                />
                <Field label="Trace ID" value={d.traceId ? <Mono>{d.traceId}</Mono> : '—'} />
                <Field label="Data classification" value={d.classification ?? '—'} />
                <Field
                  label="LLM cost"
                  value={`USD ${d.totals.costUsd.toFixed(6)} · ${d.totals.inputTokens} in / ${d.totals.outputTokens} out`}
                />
                <Field
                  label="Calls"
                  value={`${d.totals.toolCalls} tool · ${d.totals.llmCalls} LLM`}
                />
              </dl>
              {d.status === 'waiting' && (
                <p className="mt-4 rounded-md border border-warn/30 bg-warn-bg px-3 py-2 text-xs text-warn">
                  Waiting for a human decision. Decide it in{' '}
                  <Link to="/approvals" className="underline">
                    Approvals
                  </Link>
                  ; the execution resumes after the decision.
                </p>
              )}
              {output?.synthetic === true && (
                <p className="mt-4 rounded-md border border-warn/30 bg-warn-bg px-3 py-2 text-xs text-warn">
                  SYNTHETIC data: this execution used test fixtures, not a real system.
                </p>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Steps</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <Table>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Step</Th>
                    <Th>Type</Th>
                    <Th>Status</Th>
                    <Th>Attempt</Th>
                    <Th className="text-right">Duration</Th>
                    <Th>Error</Th>
                  </tr>
                </thead>
                <tbody>
                  {d.steps.map((s) => (
                    <tr key={s.key}>
                      <Td className="text-muted tabular-nums">{s.sequence}</Td>
                      <Td>{s.key}</Td>
                      <Td>
                        <Mono>{s.type}</Mono>
                      </Td>
                      <Td>
                        <DomainStatus value={s.status} />
                      </Td>
                      <Td className="tabular-nums">{s.attempt}</Td>
                      <Td className="text-right text-muted tabular-nums">
                        {durationMs(s.startedAt, s.completedAt)}
                      </Td>
                      <Td className="max-w-md">
                        <Mono>{s.error ? `${s.error.code}: ${s.error.message}` : '—'}</Mono>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
          <ContextPacks executionId={d.executionId} />
          {d.toolCalls.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Tool calls</CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <Table>
                  <thead>
                    <tr>
                      <Th>Step</Th>
                      <Th>Connection · capability</Th>
                      <Th>Implementation</Th>
                      <Th>Status</Th>
                      <Th>Classification</Th>
                      <Th className="text-right">Duration</Th>
                      <Th>Result hash / error</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.toolCalls.map((c) => (
                      <tr key={c.id}>
                        <Td>{c.stepKey ?? '—'}</Td>
                        <Td>
                          {c.connectionName} · <Mono>{c.capability}</Mono>
                        </Td>
                        <Td>
                          <Mono>{c.implementation}</Mono>
                        </Td>
                        <Td>
                          <DomainStatus value={c.status} />
                        </Td>
                        <Td>{c.resultClassification ?? '—'}</Td>
                        <Td className="text-right tabular-nums text-muted">
                          {c.durationMs !== null ? `${c.durationMs} ms` : '—'}
                        </Td>
                        <Td>
                          <Mono>
                            {c.error
                              ? `${c.error.code}: ${c.error.message}`
                              : `${c.resultHash?.slice(0, 16) ?? ''}…`}
                          </Mono>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}
          {d.llmCalls.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>LLM calls</CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <Table>
                  <thead>
                    <tr>
                      <Th>Step</Th>
                      <Th>Prompt</Th>
                      <Th>Profile → provider / model</Th>
                      <Th>Classification</Th>
                      <Th>Status</Th>
                      <Th className="text-right">Tokens</Th>
                      <Th className="text-right">Cost (USD)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.llmCalls.map((c) => (
                      <tr key={c.id}>
                        <Td>
                          {c.stepKey ?? '—'}
                          {c.attempt > 1 && (
                            <span className="text-muted"> (attempt {c.attempt})</span>
                          )}
                        </Td>
                        <Td>
                          {c.promptKey ? <Mono>{`${c.promptKey}@${c.promptVersion}`}</Mono> : '—'}
                        </Td>
                        <Td>
                          <Mono>{`${c.modelProfile} → ${c.provider} / ${c.model}`}</Mono>
                        </Td>
                        <Td>{c.classification}</Td>
                        <Td>
                          <DomainStatus value={c.status} />
                          {c.refusalReason && (
                            <p className="mt-1 text-xs text-muted">{c.refusalReason}</p>
                          )}
                          {c.error && (
                            <p className="mt-1 text-xs text-muted">
                              {c.error.code}
                              {c.error.issues.length > 0 &&
                                `: ${c.error.issues.map((i) => `${i.path} (${i.code})`).join(', ')}`}
                            </p>
                          )}
                        </Td>
                        <Td className="text-right tabular-nums text-muted">
                          {c.inputTokens} / {c.outputTokens}
                        </Td>
                        <Td className="text-right tabular-nums">{c.costUsd.toFixed(6)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}
          {!d.error && isIfsDraft((d.output as { ifs?: unknown } | null)?.ifs) && (
            <IfsDraftView
              executionId={d.status === 'succeeded' ? d.executionId : undefined}
              draft={(d.output as { ifs: Parameters<typeof IfsDraftView>[0]['draft'] }).ifs}
            />
          )}
          <Card>
            <CardHeader>
              <CardTitle>
                {d.error ? 'Error' : d.outcome === 'stopped' ? 'Stopped result' : 'Result'}
              </CardTitle>
            </CardHeader>
            <CardBody>
              {d.error ? (
                <ErrorNote error={{ safeMessage: `${d.error.code}: ${d.error.message}` }} />
              ) : (
                <>
                  {output?.verification && (
                    <p className="mb-4 text-xs text-muted" role="status">
                      Verifier: {output.verification.verified ?? 0} of{' '}
                      {output.verification.claims ?? 0} claims VERIFIED against retrieved sources ·{' '}
                      {output.verification.downgraded ?? 0} downgraded ·{' '}
                      {output.verification.droppedEvidence ?? 0} evidence item(s) rejected
                    </p>
                  )}
                  {isIfsDraft((d.output as { ifs?: unknown } | null)?.ifs) ? (
                    <p className="text-sm text-muted">
                      The IFS draft is shown above. Raw result (JSON):
                    </p>
                  ) : null}
                  <StructuredValue value={d.output} />
                </>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Input</CardTitle>
            </CardHeader>
            <CardBody>
              <pre className="overflow-x-auto font-mono text-xs">
                {JSON.stringify(d.input, null, 2)}
              </pre>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}

interface ContextPackView {
  id: string;
  stepKey: string | null;
  coverage: { key: string; status: string; hits: number; researched: boolean; note?: string }[];
  elements: {
    kind: string;
    id: string;
    requirement: string;
    source?: { kind: string; id: string; version?: string };
  }[];
  metrics: Record<string, number>;
}

/** What the execution recalled (04 §7): coverage per requirement and learning metrics. */
function ContextPacks({ executionId }: { executionId: string }) {
  const q = useQuery({
    queryKey: ['context-packs', executionId],
    queryFn: () =>
      api<{ items: ContextPackView[] }>(`/api/v1/executions/${executionId}/context-packs`),
  });
  const packs = q.data?.items ?? [];
  if (packs.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Knowledge used</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        {packs.map((p) => (
          <div key={p.id} className="space-y-2">
            <p className="text-xs text-muted" role="status">
              Step {p.stepKey ?? '—'} · recalled {p.metrics.recallHits ?? 0} · research calls{' '}
              {p.metrics.researchCalls ?? 0} · research avoided {p.metrics.researchAvoided ?? 0} ·{' '}
              {p.metrics.elements ?? 0} element(s) in context
            </p>
            <ul className="space-y-1 text-sm">
              {p.coverage.map((c) => (
                <li key={c.key} className="flex items-center gap-2">
                  <DomainStatus
                    value={
                      c.status === 'SATISFIED'
                        ? 'ok'
                        : c.status === 'PARTIAL'
                          ? 'degraded'
                          : 'failed'
                    }
                  />
                  <Mono>{c.key}</Mono>
                  <span className="text-xs text-muted">
                    {c.status.toLowerCase()} · {c.hits} hit(s){c.researched ? ' · researched' : ''}
                    {c.note ? ` · ${c.note}` : ''}
                  </span>
                </li>
              ))}
            </ul>
            <ul className="text-xs text-muted">
              {p.elements.map((e) => (
                <li key={`${e.kind}:${e.id}`}>
                  {e.source ? (
                    <>
                      {e.source.kind} <Mono>{e.source.id}</Mono>
                      {e.source.version && <> v{e.source.version}</>}
                    </>
                  ) : (
                    <Mono>{e.id}</Mono>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
